import React from 'react';
import { ArrowLeft, Printer, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const ContractPage = () => {
  const navigate = useNavigate();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Hidden in print */}
      <div className="print:hidden bg-slate-900 text-white py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Website
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-sky-600 hover:bg-sky-700"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print / Save PDF
          </Button>
        </div>
      </div>

      {/* Contract Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 print:py-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_wfp-transit-lease/artifacts/41nr5knc_F975D878-74D0-409C-8954-495598ACD4D9.jpeg" 
            alt="WFP Sussex Vans" 
            className="h-32 sm:h-40 w-auto mx-auto mb-6 mix-blend-multiply"
          />
        </div>
        
        {/* Title */}
        <div className="text-center mb-12 print:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
            SHORT-TERM VEHICLE LEASE AGREEMENT
          </h1>
          <p className="text-lg text-slate-600">(Trade Use – User Insured)</p>
        </div>

        <div className="prose prose-slate max-w-none">
          {/* Date */}
          <p className="text-lg">
            This Agreement is made on: <span className="border-b-2 border-slate-300 px-8">________________</span>
          </p>

          {/* Section 1 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">1. PARTIES</h2>
          
          <div className="bg-slate-50 p-6 rounded-lg mb-4 print:bg-white print:border print:border-slate-200">
            <p className="font-semibold text-slate-900 mb-2">Vehicle Owner (Lessor):</p>
            <p className="mb-1">Name / Trading Name: <span className="border-b border-slate-300 inline-block w-64">________________</span></p>
            <p>Address: <span className="border-b border-slate-300 inline-block w-72">________________</span></p>
          </div>

          <div className="bg-slate-50 p-6 rounded-lg print:bg-white print:border print:border-slate-200">
            <p className="font-semibold text-slate-900 mb-2">User (Lessee):</p>
            <p className="mb-1">Name / Business Name: <span className="border-b border-slate-300 inline-block w-64">________________</span></p>
            <p>Address: <span className="border-b border-slate-300 inline-block w-72">________________</span></p>
          </div>

          {/* Section 2 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">2. VEHICLE DETAILS</h2>
          <p>The Vehicle Owner agrees to make available the following vehicle to the User under this Agreement:</p>
          
          <div className="bg-slate-50 p-6 rounded-lg my-4 print:bg-white print:border print:border-slate-200">
            <ul className="list-none space-y-2 mb-0">
              <li><strong>Vehicle:</strong> Window Cleaning Vehicle</li>
              <li><strong>Make / Model:</strong> Ford Transit Custom</li>
              <li><strong>Registration Number:</strong> <span className="border-b border-slate-300 inline-block w-32">________</span></li>
              <li><strong>Fitted Equipment:</strong>
                <ul className="list-disc ml-6 mt-2">
                  <li>650-litre water tank</li>
                  <li>Immersion heater for hot water</li>
                  <li>Two-person setup</li>
                  <li>Two electric hose reels</li>
                </ul>
              </li>
            </ul>
          </div>
          <p className="font-semibold text-amber-700">No poles, brushes, or accessories are supplied.</p>

          {/* Section 3 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">3. NATURE OF AGREEMENT</h2>
          <p><strong>3.1</strong> This Agreement is a short-term vehicle lease provided to trade users only.</p>
          <p><strong>3.2</strong> This is not a public self-drive hire service.</p>
          <p><strong>3.3</strong> The User accepts that the Vehicle Owner does not provide insurance for use of the vehicle.</p>

          {/* Section 4 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">4. LEASE PERIOD</h2>
          <p>The lease period shall run from:</p>
          <div className="bg-slate-50 p-6 rounded-lg my-4 print:bg-white print:border print:border-slate-200">
            <p className="mb-2"><strong>Start:</strong> <span className="border-b border-slate-300 inline-block w-48">________________</span></p>
            <p><strong>End:</strong> <span className="border-b border-slate-300 inline-block w-48">________________</span></p>
          </div>
          <p>Any extension must be agreed in writing before expiry.</p>

          {/* Section 5 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">5. PERMITTED USE</h2>
          <p><strong>5.1</strong> The vehicle may be used only for lawful commercial window-cleaning purposes.</p>
          <p><strong>5.2</strong> The vehicle must not be:</p>
          <ul>
            <li>Sub-leased or lent to any third party</li>
            <li>Used for any illegal or non-business purpose</li>
            <li>Driven outside the UK without written permission</li>
          </ul>
          <p><strong>5.3</strong> Only approved drivers named by the User may operate the vehicle.</p>

          {/* Section 6 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">6. INSURANCE (USER RESPONSIBILITY)</h2>
          <div className="bg-red-50 border-l-4 border-red-500 p-4 my-4 print:bg-white">
            <p className="font-semibold text-red-800 mb-2">CRITICAL SECTION</p>
          </div>
          <p><strong>6.1</strong> The User is solely responsible for arranging and maintaining motor insurance for the vehicle for the full lease period.</p>
          <p><strong>6.2</strong> The insurance must:</p>
          <ul>
            <li>Be fully comprehensive</li>
            <li>Cover the specific vehicle registration number</li>
            <li>Include commercial window-cleaning use</li>
            <li>Name the User as policyholder</li>
            <li>Note the Vehicle Owner as vehicle owner</li>
          </ul>
          <p><strong>6.3</strong> No vehicle will be released without valid insurance documentation being provided and verified.</p>
          <p><strong>6.4</strong> The Vehicle Owner does not provide self-drive hire insurance or any cover for the User while the vehicle is in use.</p>

          {/* Section 7 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">7. LIABILITY & INDEMNITY</h2>
          <p><strong>7.1</strong> The User accepts full responsibility for:</p>
          <ul>
            <li>Damage to the vehicle or fitted equipment</li>
            <li>Loss or theft of the vehicle or equipment</li>
            <li>Third-party claims, fines, penalties, or legal costs</li>
          </ul>
          <p><strong>7.2</strong> The User agrees to fully indemnify the Vehicle Owner against all claims, losses, costs, or expenses arising from use of the vehicle during the lease period.</p>

          {/* Section 8 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">8. VEHICLE CONDITION & MAINTENANCE</h2>
          <p><strong>8.1</strong> The vehicle is supplied in good working order.</p>
          <p><strong>8.2</strong> The User must:</p>
          <ul>
            <li>Carry out basic checks (oil, tyres, fluid levels)</li>
            <li>Keep the vehicle secure when unattended</li>
            <li>Not modify or remove any fitted equipment</li>
          </ul>
          <p>Any faults or damage must be reported immediately.</p>

          {/* Section 9 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">9. ACCIDENTS, DAMAGE & BREAKDOWNS</h2>
          <p><strong>9.1</strong> Any accident, damage, theft, or breakdown must be reported immediately to:</p>
          <ul>
            <li>The User's insurer</li>
            <li>The Vehicle Owner</li>
            <li>The police where required</li>
          </ul>
          <p><strong>9.2</strong> The User is responsible for all insurance excesses and recovery costs.</p>

          {/* Section 10 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">10. PAYMENT & DEPOSIT</h2>
          <div className="bg-slate-50 p-6 rounded-lg my-4 print:bg-white print:border print:border-slate-200">
            <p className="mb-2"><strong>Lease Fee:</strong> £<span className="border-b border-slate-300 inline-block w-24">________</span></p>
            <p><strong>Refundable Deposit:</strong> £<span className="border-b border-slate-300 inline-block w-24">________</span></p>
          </div>
          <p>The deposit may be retained in part or full to cover:</p>
          <ul>
            <li>Damage or loss</li>
            <li>Missing equipment</li>
            <li>Excessive cleaning</li>
            <li>Late return</li>
          </ul>

          {/* Section 11 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">11. TERMINATION</h2>
          <p>The Vehicle Owner may terminate this Agreement immediately if:</p>
          <ul>
            <li>Insurance becomes invalid or withdrawn</li>
            <li>The vehicle is misused</li>
            <li>Payment terms are breached</li>
          </ul>
          <p>Upon termination, the vehicle must be returned immediately.</p>

          {/* Section 12 */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">12. GOVERNING LAW</h2>
          <p>This Agreement shall be governed by and construed in accordance with the laws of England and Wales.</p>

          {/* Section 13 - Signatures */}
          <h2 className="text-xl font-bold mt-10 mb-4 text-slate-900">13. SIGNATURES</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mt-6">
            <div className="bg-slate-50 p-6 rounded-lg print:bg-white print:border print:border-slate-200">
              <p className="font-semibold text-slate-900 mb-6">Vehicle Owner (Lessor):</p>
              <p className="mb-4">Signature: <span className="border-b border-slate-400 inline-block w-full mt-8">&#8203;</span></p>
              <p className="mb-4">Name: <span className="border-b border-slate-300 inline-block w-48">________________</span></p>
              <p>Date: <span className="border-b border-slate-300 inline-block w-32">________</span></p>
            </div>

            <div className="bg-slate-50 p-6 rounded-lg print:bg-white print:border print:border-slate-200">
              <p className="font-semibold text-slate-900 mb-6">User (Lessee):</p>
              <p className="mb-4">Signature: <span className="border-b border-slate-400 inline-block w-full mt-8">&#8203;</span></p>
              <p className="mb-4">Name: <span className="border-b border-slate-300 inline-block w-48">________________</span></p>
              <p>Date: <span className="border-b border-slate-300 inline-block w-32">________</span></p>
            </div>
          </div>

          {/* Footer Notice */}
          <div className="mt-12 pt-6 border-t-2 border-slate-200 text-center text-sm text-slate-500">
            <p>Trade users only. Vehicles are provided under a short-term lease agreement and are insured by the user.</p>
            <p>This is not a public self-drive hire service.</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:bg-white { background-color: white !important; }
          .print\\:border { border: 1px solid #e2e8f0 !important; }
          .print\\:border-slate-200 { border-color: #e2e8f0 !important; }
          .print\\:py-8 { padding-top: 2rem !important; padding-bottom: 2rem !important; }
        }
      `}</style>
    </div>
  );
};

export default ContractPage;
